import * as React from 'react';
import { createPortal } from 'react-dom';
import {
  buildPageHelpObservations,
  getPageHelpAnchorElement,
  PAGE_HELP_OPEN_EVENT,
  PAGE_HELP_REGISTER_EVENT,
  PAGE_HELP_UNREGISTER_EVENT,
  PageHelpAction,
  PageHelpAnchor,
  PageHelpDefinition,
  subscribePageHelpDefinitions
} from 'Src/helpers/pageHelp/pageHelp';
import { getHelpContext, HelpContextResponse, HelpKnowledgeArticle } from 'Src/helpers/service/help/helpKnowledge';
import { SQUADY_ICON_SRC } from './squadyIcon';

import './PageHelpProvider.scss';

interface Props {
  children: React.ReactNode;
  pathname?: string;
}

interface AnchorRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

type HelpDrawerTab = 'live' | 'guides';

function getFallbackPageTitle(pathname: string): string {
  const segments = String(pathname || '')
    .split('/')
    .filter(Boolean)
    .filter(segment => !/^\d+$/.test(segment) && !/^[a-f\d]{24}$/i.test(segment));
  const raw = segments[segments.length - 1] || 'page';
  return decodeURIComponent(raw)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, character => character.toUpperCase());
}

function getVisiblePageHeading(): string {
  const headings = Array.from(document.querySelectorAll('h1')) as HTMLElement[];
  const visibleHeading = headings.find(heading => {
    const rect = heading.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && Boolean(heading.textContent && heading.textContent.trim());
  });
  return visibleHeading && visibleHeading.textContent ? visibleHeading.textContent.trim() : '';
}

function buildLearningDefinition(pathname: string, pageTitle: string): PageHelpDefinition {
  const title = pageTitle || getFallbackPageTitle(pathname);
  const screenId = `learning.${String(pathname || 'page')
    .replace(/^\/+|\/+$/g, '')
    .replace(/[^A-Za-z0-9._:-]+/g, '.') || 'page'}`;
  return {
    screenId,
    title,
    subtitle: 'A first hello from Squady while the full guide for this page is being prepared.',
    entity: { singular: 'page', plural: 'pages' },
    observations: [
      {
        id: 'squady-learning',
        tone: 'neutral',
        title: 'Squady is still learning this page',
        text:
          'I have my notebook open and I am busy sniffing out every useful button, shortcut and tricky corner. Proper step-by-step help is on the way.'
      }
    ],
    sections: [
      {
        id: 'learning-note',
        title: 'This page is on my learning list',
        summary:
          'Soon I will be able to explain what everything does, point to the right control and suggest the quickest next step. Please check back after another biscuit or two.'
      }
    ]
  };
}

function getRect(element: HTMLElement): AnchorRect {
  const rect = element.getBoundingClientRect();
  return {
    top: Math.max(4, rect.top - 6),
    left: Math.max(4, rect.left - 6),
    width: Math.max(0, rect.width + 12),
    height: Math.max(0, rect.height + 12)
  };
}

interface KnowledgeArticleCardProps {
  article: HelpKnowledgeArticle;
  isExpanded: boolean;
  onToggle: () => void;
  findAnchor: (anchorId: string) => PageHelpAnchor | undefined;
  findAction: (actionId: string) => PageHelpAction | undefined;
  onShowAnchor: (anchorId: string) => void;
  onRunAction: (action: PageHelpAction) => void;
}

function KnowledgeArticleCard({
  article,
  isExpanded,
  onToggle,
  findAnchor,
  findAction,
  onShowAnchor,
  onRunAction
}: KnowledgeArticleCardProps): React.ReactElement {
  const linkedActions = (article.actionIds || [])
    .map(findAction)
    .filter((action): action is PageHelpAction => Boolean(action));

  return (
    <article className={`squadyArticle ${isExpanded ? 'is-expanded' : ''}`}>
      <button type="button" className="squadyArticle__toggle" onClick={onToggle} aria-expanded={isExpanded}>
        <span>
          {article.categories && article.categories.length > 0 && <small>{article.categories.join(' · ')}</small>}
          <strong>{article.title}</strong>
          {article.summary && <span>{article.summary}</span>}
        </span>
        <b aria-hidden="true">{isExpanded ? '−' : '+'}</b>
      </button>

      {isExpanded && (
        <div className="squadyArticle__content">
          {(article.sections || []).map(section => {
            const anchor = section.anchorId ? findAnchor(section.anchorId) : undefined;
            const action = section.actionId ? findAction(section.actionId) : undefined;
            return (
              <section key={section.id}>
                <div className="squadyArticle__sectionHeading">
                  <h4>{section.title}</h4>
                  {anchor && (
                    <button type="button" onClick={() => onShowAnchor(anchor.id)}>
                      Show me
                    </button>
                  )}
                  {action && !action.disabled && (
                    <button type="button" onClick={() => onRunAction(action)}>
                      Open
                    </button>
                  )}
                </div>
                {section.summary && <p>{section.summary}</p>}
                {section.items && section.items.length > 0 && (
                  <ul>
                    {section.items.map((item, index) => (
                      <li key={`${section.id}-${index}`}>{item}</li>
                    ))}
                  </ul>
                )}
              </section>
            );
          })}

          {article.faq && article.faq.length > 0 && (
            <section className="squadyArticle__faq">
              <h4>Frequently asked questions</h4>
              {article.faq.map((item, index) => (
                <div key={`${article.key}-faq-${index}`}>
                  <strong>{item.question}</strong>
                  <p>{item.answer}</p>
                </div>
              ))}
            </section>
          )}

          {linkedActions.length > 0 && (
            <div className="squadyArticle__actions">
              {linkedActions.map(action => (
                <button key={action.id} type="button" disabled={action.disabled} onClick={() => onRunAction(action)}>
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </article>
  );
}

export function PageHelpProvider({ children, pathname = window.location.pathname }: Props): React.ReactElement {
  const [definition, setDefinition] = React.useState<PageHelpDefinition | null>(null);
  const [fallbackPageTitle, setFallbackPageTitle] = React.useState(() => getFallbackPageTitle(pathname));
  const [isOpen, setIsOpen] = React.useState(false);
  const [isInspecting, setIsInspecting] = React.useState(false);
  const [activeAnchorId, setActiveAnchorId] = React.useState<string | null>(null);
  const [anchorRect, setAnchorRect] = React.useState<AnchorRect | null>(null);
  const [activeTab, setActiveTab] = React.useState<HelpDrawerTab>('live');
  const [knowledge, setKnowledge] = React.useState<HelpContextResponse | null>(null);
  const [isKnowledgeLoading, setIsKnowledgeLoading] = React.useState(false);
  const [hasKnowledgeError, setHasKnowledgeError] = React.useState(false);
  const [expandedArticleKey, setExpandedArticleKey] = React.useState<string | null>(null);
  const definitionRef = React.useRef<PageHelpDefinition | null>(null);
  const closeButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const returnFocusRef = React.useRef<HTMLElement | null>(null);
  const drawerBodyRef = React.useRef<HTMLDivElement | null>(null);
  const mapAnchorRefs = React.useRef<{ [anchorId: string]: HTMLButtonElement | null }>({});
  const fallbackDefinition = React.useMemo(
    () => buildLearningDefinition(pathname, fallbackPageTitle),
    [fallbackPageTitle, pathname]
  );
  const activeDefinition = definition || fallbackDefinition;

  React.useEffect(() => subscribePageHelpDefinitions(setDefinition), []);

  React.useEffect(() => {
    definitionRef.current = activeDefinition;
    const source = definition ? 'squady' : 'squady-fallback';
    window.dispatchEvent(
      new CustomEvent(PAGE_HELP_REGISTER_EVENT, {
        detail: { label: `Ask Squady about ${activeDefinition.title}`, text: 'Ask Squady', source }
      })
    );
    return () => {
      window.dispatchEvent(new CustomEvent(PAGE_HELP_UNREGISTER_EVENT, { detail: { source } }));
    };
  }, [activeDefinition.screenId, activeDefinition.title, Boolean(definition)]);

  React.useEffect(() => {
    setKnowledge(null);
    setHasKnowledgeError(false);
    setExpandedArticleKey(null);
    setActiveTab('live');
  }, [activeDefinition.screenId]);

  React.useEffect(() => {
    setFallbackPageTitle(getFallbackPageTitle(pathname));
  }, [pathname]);

  React.useEffect(() => {
    const onOpen = (event: Event) => {
      const source = (event as CustomEvent<{ source?: string }>).detail?.source;
      if (source === 'legacy') return;
      if (definitionRef.current) {
        returnFocusRef.current = document.activeElement as HTMLElement;
        if (!definition) setFallbackPageTitle(getVisiblePageHeading() || getFallbackPageTitle(pathname));
        setIsOpen(true);
      }
    };
    window.addEventListener(PAGE_HELP_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(PAGE_HELP_OPEN_EVENT, onOpen);
  }, [definition, pathname]);

  React.useEffect(() => {
    if (!isOpen || !definition) return undefined;
    let cancelled = false;
    setIsKnowledgeLoading(true);
    setHasKnowledgeError(false);
    getHelpContext(definition.screenId)
      .then(result => {
        if (cancelled) return;
        setKnowledge(result);
        setExpandedArticleKey(current => current || (result.articles[0] && result.articles[0].key) || null);
      })
      .catch(() => {
        if (!cancelled) setHasKnowledgeError(true);
      })
      .then(() => {
        if (!cancelled) setIsKnowledgeLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, definition && definition.screenId]);

  const closeHelp = React.useCallback(() => {
    setIsOpen(false);
    setIsInspecting(false);
    setActiveAnchorId(null);
    setAnchorRect(null);
    if (returnFocusRef.current && document.body.contains(returnFocusRef.current)) {
      returnFocusRef.current.focus();
    }
  }, []);

  React.useEffect(() => {
    if (isOpen && closeButtonRef.current) closeButtonRef.current.focus();
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) return undefined;
    const onOutsideMouseDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target && target.closest('.squadyDrawer')) return;
      closeHelp();
    };
    document.addEventListener('mousedown', onOutsideMouseDown);
    return () => document.removeEventListener('mousedown', onOutsideMouseDown);
  }, [closeHelp, isOpen]);

  React.useEffect(() => {
    if (!isOpen || !activeAnchorId || activeTab !== 'live') return undefined;
    const frame = window.requestAnimationFrame(() => {
      const body = drawerBodyRef.current;
      const explanation = mapAnchorRefs.current[activeAnchorId];
      if (!body || !explanation) return;
      const bodyRect = body.getBoundingClientRect();
      const explanationRect = explanation.getBoundingClientRect();
      const targetTop =
        body.scrollTop + (explanationRect.top - bodyRect.top) - body.clientHeight / 2 + explanationRect.height / 2;
      body.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [activeAnchorId, activeTab, isOpen]);

  const findAnchor = React.useCallback(
    (anchorId: string): PageHelpAnchor | undefined =>
      activeDefinition.anchors ? activeDefinition.anchors.find(anchor => anchor.id === anchorId) : undefined,
    [activeDefinition]
  );

  const findAction = React.useCallback(
    (actionId: string): PageHelpAction | undefined =>
      activeDefinition.actions ? activeDefinition.actions.find(action => action.id === actionId) : undefined,
    [activeDefinition]
  );

  const showAnchor = React.useCallback(
    (anchorId: string, shouldScroll = true) => {
      const anchor = findAnchor(anchorId);
      if (!anchor) return;
      const element = getPageHelpAnchorElement(anchor);
      if (!element) return;
      if (shouldScroll) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }
      setActiveAnchorId(anchorId);
      window.requestAnimationFrame(() => setAnchorRect(getRect(element)));
    },
    [findAnchor]
  );

  React.useEffect(() => {
    if (!activeAnchorId) return undefined;
    const updateAnchorRect = () => {
      const anchor = findAnchor(activeAnchorId);
      const element = anchor ? getPageHelpAnchorElement(anchor) : null;
      setAnchorRect(element ? getRect(element) : null);
    };
    window.addEventListener('resize', updateAnchorRect);
    document.addEventListener('scroll', updateAnchorRect, true);
    const timer = window.setTimeout(updateAnchorRect, 260);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', updateAnchorRect);
      document.removeEventListener('scroll', updateAnchorRect, true);
    };
  }, [activeAnchorId, findAnchor]);

  React.useEffect(() => {
    if (!activeAnchorId || isOpen || isInspecting) return undefined;

    const dismissAnchorHighlight = () => {
      setActiveAnchorId(null);
      setAnchorRect(null);
    };
    const onPageInteraction = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (target && target.closest('.squadyDrawer')) return;
      dismissAnchorHighlight();
    };
    const timer = window.setTimeout(dismissAnchorHighlight, 2200);

    document.addEventListener('mousedown', onPageInteraction, true);
    document.addEventListener('focusin', onPageInteraction, true);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', onPageInteraction, true);
      document.removeEventListener('focusin', onPageInteraction, true);
    };
  }, [activeAnchorId, isInspecting, isOpen]);

  React.useEffect(() => {
    if (!isInspecting || !activeDefinition.anchors) return undefined;
    const anchorIds = new Set(activeDefinition.anchors.map(anchor => anchor.id));
    document.body.classList.add('squadyInspectMode');

    const getTargetAnchor = (event: Event): HTMLElement | null => {
      const target = event.target as HTMLElement;
      if (!target || target.closest('.squadyDrawer')) return null;
      const anchorElement = target.closest('[data-help-anchor]') as HTMLElement | null;
      return anchorElement && anchorIds.has(anchorElement.dataset.helpAnchor || '') ? anchorElement : null;
    };
    const onMove = (event: MouseEvent) => {
      const element = getTargetAnchor(event);
      if (!element) {
        setActiveAnchorId(null);
        setAnchorRect(null);
        return;
      }
      setActiveAnchorId(element.dataset.helpAnchor || null);
      setAnchorRect(getRect(element));
    };
    const onClick = (event: MouseEvent) => {
      const element = getTargetAnchor(event);
      if (!element) return;
      event.preventDefault();
      event.stopPropagation();
      setActiveAnchorId(element.dataset.helpAnchor || null);
      setAnchorRect(getRect(element));
      setIsInspecting(false);
      setActiveTab('live');
      setIsOpen(true);
    };
    document.addEventListener('mousemove', onMove, true);
    document.addEventListener('click', onClick, true);
    return () => {
      document.body.classList.remove('squadyInspectMode');
      document.removeEventListener('mousemove', onMove, true);
      document.removeEventListener('click', onClick, true);
    };
  }, [activeDefinition, isInspecting]);

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (isInspecting || activeAnchorId) {
        setIsInspecting(false);
        setActiveAnchorId(null);
        setAnchorRect(null);
      } else if (isOpen) {
        closeHelp();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [activeAnchorId, closeHelp, isInspecting, isOpen]);

  const runAction = (action: PageHelpAction) => {
    if (action.disabled) return;
    setIsOpen(false);
    setActiveAnchorId(null);
    setAnchorRect(null);
    window.requestAnimationFrame(() => {
      action.run();
    });
  };

  const showKnowledgeAnchor = (anchorId: string) => {
    setIsOpen(false);
    showAnchor(anchorId);
  };

  const observations = buildPageHelpObservations(activeDefinition);
  const activeAnchor = activeAnchorId ? findAnchor(activeAnchorId) : undefined;
  const filters = activeDefinition.state && activeDefinition.state.filters ? activeDefinition.state.filters : [];
  const contextArticles = knowledge ? knowledge.articles : [];

  const overlay =
    isOpen || isInspecting || anchorRect
      ? createPortal(
          <>
            {anchorRect && (
              <div
                className="squadySpotlight"
                style={{
                  top: anchorRect.top,
                  left: anchorRect.left,
                  width: anchorRect.width,
                  height: anchorRect.height
                }}
                aria-hidden="true"
              >
                {activeAnchor && <span>{activeAnchor.label}</span>}
              </div>
            )}

            {isInspecting && (
              <div className="squadyInspectHint" role="status">
                Point to a highlighted part of the page and click it for help. Press Esc to stop.
              </div>
            )}

            {isOpen && (
              <aside className="squadyDrawer" role="dialog" aria-modal="false" aria-labelledby="squady-title">
                <header className="squadyDrawer__header">
                  <div className="squadyDrawer__brand">
                    <span className="squadyDrawer__brandIcon">
                      <img src={SQUADY_ICON_SRC} alt="" aria-hidden="true" />
                    </span>
                    <div>
                      <span>Ask Squady</span>
                      <h2 id="squady-title">{activeDefinition.title}</h2>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="squadyDrawer__close"
                    onClick={closeHelp}
                    aria-label="Close help"
                    ref={closeButtonRef}
                  >
                    ×
                  </button>
                  {activeDefinition.subtitle && <p>{activeDefinition.subtitle}</p>}
                </header>

                {definition && (
                  <nav className="squadyDrawer__tabs" aria-label="Squady help sections">
                    <button
                      type="button"
                      className={activeTab === 'live' ? 'is-active' : ''}
                      onClick={() => setActiveTab('live')}
                    >
                      Live help
                    </button>
                    <button
                      type="button"
                      className={activeTab === 'guides' ? 'is-active' : ''}
                      onClick={() => setActiveTab('guides')}
                    >
                      Guides &amp; FAQ
                      {contextArticles.length > 0 && <span>{contextArticles.length}</span>}
                    </button>
                  </nav>
                )}

                <div className="squadyDrawer__body" ref={drawerBodyRef}>
                  {activeTab === 'live' ? (
                    <>
                      <section className="squadyDrawer__section" aria-labelledby="squady-now">
                        <div className="squadyDrawer__sectionHeading">
                          <div>
                            <span>Live page check</span>
                            <h3 id="squady-now">What is happening now</h3>
                          </div>
                          <span className="squadyDrawer__live">Live</span>
                        </div>
                        <div className="squadyObservations">
                          {observations.map(observation => (
                            <article
                              key={observation.id}
                              className={`squadyObservation squadyObservation--${observation.tone}`}
                            >
                              <strong>{observation.title}</strong>
                              <p>{observation.text}</p>
                            </article>
                          ))}
                        </div>
                        {filters.length > 0 && (
                          <div className="squadyFilters">
                            <span>Active filters</span>
                            <div>
                              {filters.map(filter => (
                                <span key={filter.id}>
                                  {filter.label}
                                  {filter.value ? `: ${filter.value}` : ''}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </section>

                      {activeDefinition.actions && activeDefinition.actions.length > 0 && (
                        <section className="squadyDrawer__section" aria-labelledby="squady-actions">
                          <span className="squadyDrawer__eyebrow">Quick actions</span>
                          <h3 id="squady-actions">What you can do</h3>
                          <div className="squadyActions">
                            {activeDefinition.actions.map(action => (
                              <button
                                key={action.id}
                                type="button"
                                className={action.kind === 'primary' ? 'is-primary' : ''}
                                disabled={action.disabled}
                                onClick={() => runAction(action)}
                              >
                                <span>
                                  <strong>{action.label}</strong>
                                  {action.description && <small>{action.description}</small>}
                                </span>
                                <b aria-hidden="true">›</b>
                              </button>
                            ))}
                          </div>
                        </section>
                      )}

                      {activeDefinition.anchors && activeDefinition.anchors.length > 0 && (
                        <section className="squadyDrawer__section" aria-labelledby="squady-map">
                          <div className="squadyDrawer__sectionHeading">
                            <div>
                              <span>Screen map</span>
                              <h3 id="squady-map">Show me this page</h3>
                            </div>
                            <button
                              type="button"
                              className={`squadyInspectButton ${isInspecting ? 'is-active' : ''}`}
                              onClick={() => {
                                setActiveAnchorId(null);
                                setAnchorRect(null);
                                setIsInspecting(true);
                                setIsOpen(false);
                              }}
                            >
                              What's this?
                            </button>
                          </div>
                          <div className="squadyMap">
                            {activeDefinition.anchors.map(anchor => (
                              <button
                                key={anchor.id}
                                type="button"
                                className={activeAnchorId === anchor.id ? 'is-active' : ''}
                                aria-current={activeAnchorId === anchor.id ? 'true' : undefined}
                                ref={element => {
                                  mapAnchorRefs.current[anchor.id] = element;
                                }}
                                onClick={() => {
                                  showAnchor(anchor.id);
                                  setIsOpen(false);
                                }}
                              >
                                <span aria-hidden="true">◎</span>
                                <span>
                                  <strong>{anchor.label}</strong>
                                  <small>{anchor.description}</small>
                                </span>
                              </button>
                            ))}
                          </div>
                        </section>
                      )}
                    </>
                  ) : (
                    <>
                      {isKnowledgeLoading && (
                        <div className="squadyKnowledgeStatus" role="status">
                          <span /> Loading help…
                        </div>
                      )}

                      <div className="squadyArticles">
                        {contextArticles.map(article => (
                          <KnowledgeArticleCard
                            key={`${article.key}-${article.version || 1}`}
                            article={article}
                            isExpanded={expandedArticleKey === article.key}
                            onToggle={() =>
                              setExpandedArticleKey(current => (current === article.key ? null : article.key))
                            }
                            findAnchor={findAnchor}
                            findAction={findAction}
                            onShowAnchor={showKnowledgeAnchor}
                            onRunAction={runAction}
                          />
                        ))}
                      </div>

                      {contextArticles.length === 0 &&
                        !isKnowledgeLoading &&
                        activeDefinition.sections &&
                        activeDefinition.sections.map(section => (
                          <section className="squadyDrawer__section" key={section.id}>
                            <h3>{section.title}</h3>
                            {section.summary && <p className="squadyDrawer__summary">{section.summary}</p>}
                            {section.items && (
                              <ul>
                                {section.items.map((item, index) => (
                                  <li key={`${section.id}-${index}`}>{item}</li>
                                ))}
                              </ul>
                            )}
                          </section>
                        ))}

                      {hasKnowledgeError && (
                        <p className="squadyKnowledgeFallback">
                          Showing the built-in guide for this page.
                        </p>
                      )}
                    </>
                  )}
                </div>
              </aside>
            )}
          </>,
          document.body
        )
      : null;

  return (
    <>
      {children}
      {overlay}
    </>
  );
}
